import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { ApiError, unwrap } from '@/api/envelope';
import { uploadDocument } from '@/api/generated';
import { applyServerErrors } from '@/api/serverErrors';
import { FileDropZone } from '@/components/form/FileDropZone';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { useToast } from '@/components/ui/useToast';

import { CategorySelectField } from './CategorySelectField';
import {
  DOCUMENT_DESCRIPTION_MAX,
  DOCUMENT_TITLE_MAX,
  documentFormSchema,
  type DocumentFormInput,
  type DocumentFormValues,
} from './documentSchemas';
import { documentFieldRefusal } from './documentRefusals';
import { acceptAttribute, refuseFile, typesInWords } from './uploadRules';
import { useInvalidateDocuments, useUploadPolicy } from './useDocuments';

/** The server's field names to the form's, in page order. */
const SERVER_FIELDS = {
  title: 'title',
  description: 'description',
  category_id: 'categoryId',
} as const;

interface UploadDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  /** After a 201: the page may use it to say where the row went. */
  onUploaded?: () => void;
}

/**
 * Upload Document (PB4): a file, a title, a description, a category, and two
 * ways to send it - Save as draft and Publish Document (P7).
 *
 * The file is held outside the form's schema and judged by `uploadRules`
 * against the policy from `GET /config`, so nothing here knows a type or a
 * size of its own (P8, I6). Until that policy arrives the drop zone is
 * disabled and says so; if it cannot be read, a retry stands in its place.
 *
 * A refusal goes where it belongs (design "Where each refusal goes"): a 413
 * or 415 against the file, which is cleared; a 422 under the field it names;
 * a 403 and anything else to an alert inside the dialog, so what was typed
 * is kept.
 */
export function UploadDocumentDialog({
  open,
  onClose,
  onUploaded,
}: UploadDocumentDialogProps) {
  const invalidate = useInvalidateDocuments();
  const toast = useToast();
  const policy = useUploadPolicy();

  const id = useId();
  const formId = `${id}-form`;

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const alertRef = useRef<HTMLDivElement>(null);
  // Which submit was pressed; set by its click, read by the one submit handler.
  const publishRef = useRef(false);

  const {
    register,
    control,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isDirty, isValid },
  } = useForm<DocumentFormInput, unknown, DocumentFormValues>({
    resolver: zodResolver(documentFormSchema),
    mode: 'onChange',
    defaultValues: { title: '', description: '', categoryId: '' },
  });

  const titleTyped = useWatch({ control, name: 'title' }).length;
  const descriptionTyped = useWatch({ control, name: 'description' }).length;

  useEffect(() => {
    if (failure) alertRef.current?.focus();
  }, [failure]);

  const upload = useMutation({
    mutationFn: async ({
      publish,
      ...values
    }: DocumentFormValues & { publish: boolean }) => {
      const reply = await uploadDocument({
        body: {
          file: file as File,
          title: values.title,
          description: values.description || undefined,
          category_id: values.categoryId || undefined,
          publish,
        },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onMutate: () => setFailure(null),
    onSuccess: async () => {
      onUploaded?.();
      onClose();
      toast.show({ message: 'Uploaded. Scanning…' });
      await invalidate();
    },
    onError: async (error) => {
      if (error instanceof ApiError && (error.status === 413 || error.status === 415)) {
        setFile(null);
        setFileError(error.serverMessage ?? error.message);
        return;
      }
      if (error instanceof ApiError && error.status === 403) {
        // Said here with what was typed kept (error contract: 403 is inline),
        // and the library asked for again so the offered actions are reviewed.
        setFailure(error.message);
        await invalidate();
        return;
      }
      const refused = documentFieldRefusal(error);
      if (refused) {
        setError(refused.field, { type: 'server', message: refused.message });
        setFocus(refused.field);
        return;
      }
      const { remainder } = applyServerErrors(
        error,
        { setError, setFocus },
        SERVER_FIELDS,
      );
      if (remainder) setFailure(remainder);
    },
  });

  const dirty = isDirty || file !== null;
  const askToClose = () => {
    if (upload.isPending) return;
    if (dirty) setConfirmingClose(true);
    else onClose();
  };

  function choose(chosen: File) {
    if (!policy.data) return;
    const refused = refuseFile(chosen, policy.data);
    setFileError(refused);
    setFile(refused ? null : chosen);
  }

  const canSubmit = isValid && file !== null && !upload.isPending;
  const hint = policy.data
    ? `${typesInWords(policy.data)}, up to ${policy.data.maxUploadMb} MB.`
    : 'Checking which files are accepted…';

  return (
    <Dialog
      open={open}
      onClose={askToClose}
      title="Upload Document"
      description="The file is scanned before it can be seen. Publish it now or keep it as a draft."
      testId="upload-document-dialog"
      actions={
        <>
          <Button
            variant="secondary"
            onClick={askToClose}
            disabled={upload.isPending}
            data-testid="upload-document-dialog-cancel-button"
          >
            Cancel
          </Button>
          {/* First of the two in order, so Enter in a field means draft. */}
          <Button
            type="submit"
            form={formId}
            variant="secondary"
            disabled={!canSubmit}
            loading={upload.isPending && upload.variables?.publish === false}
            onClick={() => {
              publishRef.current = false;
            }}
            data-testid="upload-document-dialog-draft-button"
          >
            Save as draft
          </Button>
          <Button
            type="submit"
            form={formId}
            disabled={!canSubmit}
            loading={upload.isPending && upload.variables?.publish === true}
            onClick={() => {
              publishRef.current = true;
            }}
            data-testid="upload-document-dialog-publish-button"
          >
            Publish Document
          </Button>
        </>
      }
    >
      <Stack
        component="form"
        id={formId}
        spacing={2.5}
        aria-label="Upload Document"
        data-testid="upload-document-dialog-form"
        noValidate
        onSubmit={(event) => {
          void handleSubmit((values) =>
            upload.mutate({ ...values, publish: publishRef.current }),
          )(event);
        }}
        sx={{ pt: 0.5 }}
      >
        {failure ? (
          <Alert
            severity="error"
            role="alert"
            ref={alertRef}
            tabIndex={-1}
            data-testid="upload-document-dialog-error"
          >
            {failure}
          </Alert>
        ) : null}

        {policy.isError ? (
          <ErrorState
            title="The upload rules could not be loaded."
            onRetry={() => void policy.refetch()}
            testId="upload-document-dialog-policy"
          />
        ) : (
          <FileDropZone
            label="Document file"
            hint={hint}
            accept={policy.data ? acceptAttribute(policy.data) : ''}
            value={file}
            onChange={choose}
            onRemove={() => {
              setFile(null);
              setFileError(null);
            }}
            error={fileError}
            disabled={!policy.data}
            disabledReason="Loading the upload rules…"
            busy={upload.isPending}
            busyMessage={file ? `Uploading ${file.name}` : undefined}
            testId="upload-document-dialog-file"
          />
        )}

        <Field
          label="Title"
          hint={`${titleTyped} of ${DOCUMENT_TITLE_MAX} characters`}
          error={errors.title?.message}
          testId="upload-document-dialog-title"
        >
          <TextField {...register('title')} size="small" fullWidth />
        </Field>

        <Field
          label="Description"
          hint={`${descriptionTyped} of ${DOCUMENT_DESCRIPTION_MAX} characters`}
          error={errors.description?.message}
          testId="upload-document-dialog-description"
        >
          <TextField
            {...register('description')}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </Field>

        <Controller
          name="categoryId"
          control={control}
          render={({ field }) => (
            <CategorySelectField
              value={field.value ?? ''}
              onChange={field.onChange}
              inputRef={field.ref}
              error={errors.categoryId?.message}
              testId="upload-document-dialog-category"
            />
          )}
        />
      </Stack>

      <UnsavedChangesDialog
        blocked={confirmingClose}
        stay={() => setConfirmingClose(false)}
        leave={() => {
          setConfirmingClose(false);
          onClose();
        }}
      />
    </Dialog>
  );
}
